import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buildApiUrl,
  createExportJobRequest,
  createFormShareRequest,
  deleteFormShareRequest,
  fetchAnalyticsSummary,
  listExportJobs,
  listFormShares,
} from "../lib/api-client";
import type { AnalyticsSummary, ExportJob, FormShare } from "../types/api";

type ExportFormat = ExportJob["format"];

function FormAnalyticsPage(): JSX.Element {
  const { formId } = useParams<{ teamId: string; formId: string }>();
  const queryClient = useQueryClient();
  const [exportFormat, setExportFormat] = useState<ExportFormat>("csv");
  const [shareType, setShareType] = useState<FormShare["shareType"]>("public");
  const [sharePassword, setSharePassword] = useState("");
  const [shareExpiry, setShareExpiry] = useState<number | null>(24);
  const [shareMaxUses, setShareMaxUses] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const analyticsQuery = useQuery({
    queryKey: ["analytics-summary", formId],
    queryFn: () => fetchAnalyticsSummary(formId!),
    enabled: Boolean(formId),
  });

  const exportsQuery = useQuery({
    queryKey: ["export-jobs", formId],
    queryFn: () => listExportJobs(formId!),
    enabled: Boolean(formId),
    refetchInterval: 30_000,
  });

  const sharesQuery = useQuery({
    queryKey: ["form-shares", formId],
    queryFn: () => listFormShares(formId!),
    enabled: Boolean(formId),
  });

  const summary = analyticsQuery.data?.summary;

  const handleCopyFeedback = (success: boolean, message: string) => {
    setToast({ type: success ? "success" : "error", message });
  };

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const requestExportMutation = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error("formId missing");
      return createExportJobRequest(formId, { format: exportFormat });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["export-jobs", formId] });
      setToast({ type: "success", message: "Export request queued." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to request export.",
      });
    },
  });

  const createShareMutation = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error("formId missing");
      return createFormShareRequest(formId, {
        shareType,
        password: shareType === "password" ? sharePassword : null,
        expiresInHours: shareExpiry ?? undefined,
        maxUses: shareMaxUses ?? undefined,
      });
    },
    onSuccess() {
      setSharePassword("");
      setShareMaxUses(null);
      void queryClient.invalidateQueries({ queryKey: ["form-shares", formId] });
      setToast({ type: "success", message: "Share link created." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to create share link.",
      });
    },
  });

  const deleteShareMutation = useMutation({
    mutationFn: async (shareId: string) => {
      if (!formId) throw new Error("formId missing");
      await deleteFormShareRequest(formId, shareId);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["form-shares", formId] });
      setToast({ type: "success", message: "Share link removed." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to remove share link.",
      });
    },
  });

  const totals = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.totals).map(([metric, value]) => ({ metric, value }));
  }, [summary]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "1.5rem" }}>Analytics</h1>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            Track performance, download exports, and manage share links for this form.
          </p>
        </div>
        <div style={{ minWidth: "260px" }}>
          <h2 style={{ margin: "0 0 0.5rem", fontSize: "1rem" }}>New export</h2>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <select
              value={exportFormat}
              onChange={(event) => setExportFormat(event.target.value as ExportFormat)}
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: "10px",
                padding: "0.5rem 0.75rem",
              }}
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="xlsx">Excel</option>
              <option value="ndjson">NDJSON</option>
            </select>
            <button
              type="button"
              className="button primary"
              onClick={() => requestExportMutation.mutate()}
              disabled={requestExportMutation.isPending}
            >
              {requestExportMutation.isPending ? "Queuing…" : "Queue export"}
            </button>
          </div>
        </div>
      </header>

      {toast ? (
        <div
          style={{
            position: "fixed",
            top: "1.5rem",
            right: "1.5rem",
            background: toast.type === "success" ? "var(--color-primary)" : "var(--color-critical)",
            color: "#fff",
            padding: "0.75rem 1rem",
            borderRadius: "var(--radius-base)",
            boxShadow: "var(--shadow-md)",
            zIndex: 20,
          }}
        >
          {toast.message}
        </div>
      ) : null}

      <main style={{ padding: "2rem", display: "grid", gap: "2rem", flex: 1 }}>
        <section className="surface" style={{ padding: "1.5rem" }}>
          <header style={{ marginBottom: "1rem" }}>
            <h2 style={{ margin: 0 }}>Key metrics</h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Overview of form engagement, completions, and drop-off patterns.
            </p>
          </header>
          {totals.length === 0 ? (
            <p style={{ color: "var(--color-text-muted)" }}>Not enough data to display metrics yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
              {totals.map((item) => (
                <article
                  key={item.metric}
                  style={{
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-base)",
                    padding: "0.85rem",
                    background: "var(--color-surface)",
                  }}
                >
                  <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.8rem" }}>
                    {item.metric.replace(/_/g, " ")}
                  </p>
                  <p style={{ margin: "0.35rem 0 0", fontSize: "1.35rem", fontWeight: 600 }}>{item.value}</p>
                </article>
              ))}
            </div>
          )}
          <MetricsChart summary={summary} />
          <FunnelList summary={summary} />
        </section>

        <section className="surface" style={{ padding: "1.5rem" }}>
          <header style={{ marginBottom: "1rem" }}>
            <h2 style={{ margin: 0 }}>Exports</h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Completed exports remain available until their expiration date.
            </p>
          </header>
          <ExportsTable
            jobs={exportsQuery.data?.jobs ?? []}
            isLoading={exportsQuery.isLoading}
            onCopy={handleCopyFeedback}
          />
        </section>

        <section className="surface" style={{ padding: "1.5rem" }}>
          <header style={{ marginBottom: "1rem" }}>
            <h2 style={{ margin: 0 }}>Share links</h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Generate public, password-protected, or expiring share links.
            </p>
          </header>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void createShareMutation.mutateAsync();
            }}
            style={{ display: "grid", gap: "0.75rem", marginBottom: "1.5rem" }}
          >
            <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              <label style={labelStyle}>
                <span>Share type</span>
                <select
                  value={shareType}
                  onChange={(event) => setShareType(event.target.value as FormShare["shareType"])}
                  style={inputStyle}
                >
                  <option value="public">Public</option>
                  <option value="password">Password</option>
                  <option value="one_time">One-time</option>
                  <option value="embed">Embed</option>
                </select>
              </label>
              <label style={labelStyle}>
                <span>Password</span>
                <input
                  value={sharePassword}
                  onChange={(event) => setSharePassword(event.target.value)}
                  style={inputStyle}
                  disabled={shareType !== "password"}
                  placeholder="Required for password links"
                />
              </label>
              <label style={labelStyle}>
                <span>Expires in (hours)</span>
                <input
                  type="number"
                  min={1}
                  value={shareExpiry ?? ""}
                  onChange={(event) =>
                    setShareExpiry(event.target.value ? Number.parseInt(event.target.value, 10) : null)
                  }
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                <span>Max uses</span>
                <input
                  type="number"
                  min={1}
                  value={shareMaxUses ?? ""}
                  onChange={(event) =>
                    setShareMaxUses(event.target.value ? Number.parseInt(event.target.value, 10) : null)
                  }
                  style={inputStyle}
                />
              </label>
            </div>
            <div>
              <button
                type="submit"
                className="button primary"
                disabled={createShareMutation.isPending}
              >
                {createShareMutation.isPending ? "Creating…" : "Create share link"}
              </button>
            </div>
          </form>
          <SharesTable
            shares={sharesQuery.data?.shares ?? []}
            isLoading={sharesQuery.isLoading}
            onDelete={(shareId) => deleteShareMutation.mutate(shareId)}
            onCopy={handleCopyFeedback}
          />
        </section>
      </main>
    </div>
  );
}

function MetricsChart({ summary }: { summary: AnalyticsSummary | undefined }): JSX.Element | null {
  if (!summary || summary.series.length < 2) {
    return null;
  }
  const metricKeys = Array.from(new Set(summary.series.flatMap((point) => Object.keys(point.metrics))));
  if (metricKeys.length === 0) {
    return null;
  }
  const width = 640;
  // Auto height: base 60px + 10px per data point, capped between 60-150px
  const dataPoints = summary.series.length;
  const height = Math.min(150, Math.max(60, 40 + dataPoints * 8));
  const padding = 12;
  const colors = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
  const maxValue = Math.max(
    ...metricKeys.map((key) => Math.max(...summary.series.map((point) => point.metrics[key] ?? 0)))
  );
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return null;
  }
  return (
    <div style={{ marginTop: "1.25rem" }}>
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        {metricKeys.map((key, index) => (
          <span
            key={key}
            className="pill"
            style={{
              background: "transparent",
              border: `1px solid ${colors[index % colors.length]}`,
              color: colors[index % colors.length],
            }}
          >
            {key.replace(/_/g, " ")}
          </span>
        ))}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Analytics history chart"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height: "auto", aspectRatio: `${width} / ${height}` }}
      >
        <rect x={0} y={0} width={width} height={height} fill="transparent" stroke="var(--color-border)" rx={12} ry={12} />
        {metricKeys.map((key, index) => {
          const path = summary.series
            .map((point, idx) => {
              const value = point.metrics[key] ?? 0;
              const x =
                summary.series.length === 1
                  ? width / 2
                  : padding + (idx / (summary.series.length - 1)) * (width - padding * 2);
              const normalized = value / maxValue;
              const y = height - padding - normalized * (height - padding * 2);
              return `${idx === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
            })
            .join(" ");
          return (
            <path
              key={key}
              d={path}
              fill="none"
              stroke={colors[index % colors.length]}
              strokeWidth={2.5}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
    </div>
  );
}

function FunnelList({ summary }: { summary: AnalyticsSummary | undefined }): JSX.Element | null {
  if (!summary || summary.funnel.length === 0) {
    return null;
  }
  return (
    <div
      style={{
        marginTop: "1.5rem",
        border: "1px solid var(--color-border)",
        borderRadius: "12px",
        padding: "1rem",
      }}
    >
      <h3 style={{ margin: 0, marginBottom: "0.75rem", fontSize: "0.9rem" }}>Funnel overview</h3>
      <table className="data-table" style={{ margin: 0 }}>
        <thead>
          <tr>
            <th>Step</th>
            <th>Views</th>
            <th>Completions</th>
            <th>Drop-off</th>
          </tr>
        </thead>
        <tbody>
          {summary.funnel.map((step) => (
            <tr key={`${step.stepId}-${step.date}`}>
              <td>{step.label ?? step.stepId}</td>
              <td>{step.views}</td>
              <td>{step.completions}</td>
              <td>{step.dropOff}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExportsTable({
  jobs,
  isLoading,
  onCopy,
}: {
  jobs: ExportJob[];
  isLoading: boolean;
  onCopy: (success: boolean, message: string) => void;
}): JSX.Element {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const totalPages = Math.max(1, Math.ceil(jobs.length / pageSize));
  const paginatedJobs = jobs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset to page 1 if jobs change and current page is out of bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [jobs.length, currentPage, totalPages]);

  if (isLoading) {
    return <div style={{ color: "var(--color-text-muted)" }}>Loading exports...</div>;
  }
  if (jobs.length === 0) {
    return <div style={{ color: "var(--color-text-muted)" }}>No exports requested yet.</div>;
  }
  return (
    <div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Format</th>
            <th>Status</th>
            <th>Rows</th>
            <th>Generated</th>
            <th>Download</th>
          </tr>
        </thead>
        <tbody>
          {paginatedJobs.map((job) => (
            <tr key={job.id}>
              <td>{job.format.toUpperCase()}</td>
              <td>
                {job.status === "processing" && job.progressPercent !== undefined ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div
                      style={{
                        width: "60px",
                        height: "8px",
                        background: "var(--color-border)",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${job.progressPercent}%`,
                          height: "100%",
                          background: "var(--color-primary)",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <span style={{ fontSize: "0.75rem" }}>{job.progressPercent}%</span>
                  </div>
                ) : (
                  job.status
                )}
              </td>
              <td>{job.rowCount ?? (job.totalRecords ? `~${job.totalRecords}` : "—")}</td>
              <td>{new Date(job.updatedAt).toLocaleString()}</td>
              <td>
                {job.status === "completed" ? (
                  <DownloadActions downloadUrl={job.downloadUrl} objectKey={job.location} onCopy={onCopy} />
                ) : (
                  <span style={{ color: "var(--color-text-muted)" }}>Not ready</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={jobs.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
}

function DownloadActions({
  downloadUrl,
  objectKey,
  onCopy,
}: {
  downloadUrl?: string | null;
  objectKey?: string | null;
  onCopy: (success: boolean, message: string) => void;
}): JSX.Element {
  const resolvedUrl =
    downloadUrl && (downloadUrl.startsWith("http") ? downloadUrl : buildApiUrl(downloadUrl));

  const handleCopy = async (value: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      onCopy(true, successMessage);
    } catch {
      onCopy(false, "Unable to copy link.");
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
      {resolvedUrl ? (
        <>
          <a href={resolvedUrl} className="button ghost" target="_blank" rel="noreferrer">
            Download
          </a>
          <button
            type="button"
            className="button ghost"
            style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}
            onClick={() => void handleCopy(resolvedUrl, "Download link copied.")}
          >
            Copy link
          </button>
        </>
      ) : null}
      {objectKey ? (
        <CopyableKey
          value={objectKey}
          onCopied={(success, message) => onCopy(success, message ?? "Export key copied.")}
        />
      ) : null}
      {!resolvedUrl && !objectKey ? (
        <span style={{ color: "var(--color-text-muted)" }}>Not available</span>
      ) : null}
    </div>
  );
}

function CopyableKey({
  value,
  onCopied,
}: {
  value: string;
  onCopied?: (success: boolean, message?: string) => void;
}): JSX.Element {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      onCopied?.(true, "Copied to clipboard.");
    } catch {
      onCopied?.(false, "Unable to copy.");
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
      <code style={{ fontSize: "0.8rem" }}>{value}</code>
      <button
        type="button"
        className="button ghost"
        style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }}
        onClick={() => void handleCopy()}
      >
        Copy
      </button>
    </div>
  );
}

function SharesTable({
  shares,
  isLoading,
  onDelete,
  onCopy,
}: {
  shares: FormShare[];
  isLoading: boolean;
  onDelete: (shareId: string) => void;
  onCopy: (success: boolean, message: string) => void;
}): JSX.Element {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const totalPages = Math.max(1, Math.ceil(shares.length / pageSize));
  const paginatedShares = shares.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset to page 1 if shares change and current page is out of bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [shares.length, currentPage, totalPages]);

  if (isLoading) {
    return <div style={{ color: "var(--color-text-muted)" }}>Loading share links...</div>;
  }
  if (shares.length === 0) {
    return <div style={{ color: "var(--color-text-muted)" }}>No share links created yet.</div>;
  }
  return (
    <div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Token</th>
            <th>Expires</th>
            <th>Usage</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {paginatedShares.map((share) => (
            <tr key={share.id}>
              <td>{share.shareType}</td>
              <td>
                <CopyableKey
                  value={share.token}
                  onCopied={(success) =>
                    onCopy(success, success ? "Share token copied." : "Unable to copy token.")
                  }
                />
              </td>
              <td>{share.expiresAt ? new Date(share.expiresAt).toLocaleString() : "Never"}</td>
              <td>
                {share.usedCount}
                {share.maxUses ? ` / ${share.maxUses}` : ""}
              </td>
              <td style={{ textAlign: "right" }}>
                <button type="button" className="button ghost" onClick={() => onDelete(share.id)}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={shares.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
}

function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}): JSX.Element {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "1rem",
        padding: "0.75rem 0",
        borderTop: "1px solid var(--color-border)",
      }}
    >
      <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
        Showing {startItem}–{endItem} of {totalItems}
      </span>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          type="button"
          className="button ghost"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{ padding: "0.4rem 0.75rem", fontSize: "0.85rem" }}
        >
          ← Previous
        </button>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 0.5rem",
            fontSize: "0.85rem",
            color: "var(--color-text-muted)",
          }}
        >
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          className="button ghost"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{ padding: "0.4rem 0.75rem", fontSize: "0.85rem" }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

const labelStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.35rem",
  fontSize: "0.85rem",
  color: "var(--color-text-muted)",
  fontWeight: 500,
};

const inputStyle: CSSProperties = {
  borderRadius: "10px",
  border: "1px solid var(--color-border)",
  padding: "0.55rem 0.75rem",
  fontSize: "0.9rem",
};

export default FormAnalyticsPage;
